/* eslint-disable no-unused-vars */
/*eslint-disable eqeqeq*/
import { useState, useCallback, useRef, useContext } from "react";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { PERMISSION_TYPES, COMPANY_FEATURES, isPermissionAvailableForCompanyFeature, isCustomerContactRole } from "@routebot/constants";
import { days, days_en } from "shared/util/types";
import { getContractYearLabel } from "shared/util/relationaldata";
import { getCurrentLanguage } from "shared/utility/lang";
import i18next from "i18next";

const buildShiftLabel = (shift) => {
  const dayList = getCurrentLanguage() === "tr" ? days : days_en;
  const dayLabel = dayList.find((day) => day.value === shift.day)?.label || "";
  return `${dayLabel} - ${shift.name}`;
};

export const useStore = () => {
  const auth = useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const { sendRequest } = useHttpClient();
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);

  const [studentToParentMap, setStudentToParentMap] = useState({});
  const [parentToStudentMap, setParentToStudentMap] = useState({});
  const [userMap, setUserMap] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [studentSchools, setStudentSchools] = useState([]);
  const [personelSchools, setPersonelSchools] = useState([]);
  const [studentSchoolOptions, setStudentSchoolOptions] = useState([]);
  const [personelSchoolOptions, setPersonelSchoolOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [vehicleRoutes, setVehicleRoutes] = useState([]);
  const [vehicleRouteOptions, setVehicleRouteOptions] = useState([]);
  const [bundledRoutes, setBundledRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [relations, setRelations] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [personelOptions, setPersonelOptions] = useState([]);
  const [storeReady, setStoreReady] = useState(false);
  const [educationYears, setEducationYears] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [educationYearsOptions, setEducationYearsOptions] = useState([]);
  const [selectedEducationYearId, setSelectedEducationYearId] = useState(null);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [serviceTemplateOptions, setServiceTemplateOptions] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [contractsOptions, setContractsOptions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [vehiclePayments, setVehiclePayments] = useState([]);
  const [transferregions, setTransferRegions] = useState([]);
  const [transferregionOptions, setTransferRegionOptions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [stops, setStops] = useState([]);
  const [stopOptions, setStopOptions] = useState([]);
  const [standartPersonelServices, setStandartPersonelServices] = useState([]);
  const [standartPersonelServiceOptions, setStandartPersonelServiceOptions] = useState([]);
  const [shiftPersonelServices, setShiftPersonelServices] = useState([]);
  const [shiftPersonelServiceOptions, setShiftPersonelServiceOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [shiftUsers, setShiftUsers] = useState([]);
  const [shiftUserOptions, setShiftUserOptions] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [automationOptions, setAutomationOptions] = useState([]);
  const [driverDetails, setDriverDetails] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [attendantOptions, setAttendantOptions] = useState([]);
  const [custodies, setCustodies] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [thirdParties, setThirdParties] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [stockOptions, setStockOptions] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);

  //Accounting
  const [contracts, setContracts] = useState([]);

  const schoolsREF = useRef([]);

  // Permission check helper - checks both user permission and company feature
  const hasAccess = useCallback(
    (permissionType) => {
      const hasUserPermission = auth?.permissions?.[permissionType];
      const hasCompanyFeature = isPermissionAvailableForCompanyFeature(auth?.company?.features, permissionType);
      return hasUserPermission && hasCompanyFeature;
    },
    [auth?.permissions, auth?.company?.features],
  );

  const UpdateStudents = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_STUDENTS)) {
      return true; // No permission, skip request
    }
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/students`);

      let allStudents = responseData.students;
      setStudents(allStudents);

      const studentoptions = allStudents.map((student) => {
        return {
          value: student.id,
          label: student.name + (student?.identifier?.length ? " - " + student.identifier : ""),
        };
      });
      setStudentOptions(studentoptions);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const DeleteStudent = (studentId) => {
    const newStudents = students.filter((student) => student.id !== studentId);
    setStudents([...newStudents]);
    setStudentOptions(studentOptions.filter((item) => item.value !== studentId));
  };

  const InsertStudent = (student) => {
    setStudents([student, ...students]);
    setStudentOptions([
      ...studentOptions,
      { value: student.id, label: student.name + (student.identifier.length ? " - " + student.identifier : "") },
    ]);
  };

  const UpdateStudent = (student) => {
    const index = students.findIndex((x) => x.id === student.id);
    const newStudents = [...students];
    newStudents[index] = student;
    setStudents([...newStudents]);
    setStudentOptions(
      studentOptions.map((item) => {
        if (item.value === student.id) {
          return {
            value: student.id,
            label: student.name + (student.identifier.length ? " - " + student.identifier : ""),
          };
        }
        return item;
      }),
    );
  };

  const RetrieveDriverDetails = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/driverdetails`);
      setDriverDetails(responseData.driverdetails);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertDriverDetail = (driverDetail) => {
    setDriverDetails([driverDetail, ...driverDetails]);
  };

  const UpdateDriverDetail = (driverDetail) => {
    const index = driverDetails.findIndex((x) => x.id === driverDetail.id);
    const newDriverDetails = [...driverDetails];
    newDriverDetails[index] = driverDetail;
    setDriverDetails([...newDriverDetails]);
  };

  const DeleteDriverDetail = (driverDetailId) => {
    const newDriverDetails = driverDetails.filter((driverDetail) => driverDetail.id !== driverDetailId);
    setDriverDetails([...newDriverDetails]);
  };

  const RetrieveAttendants = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/attendants`);
      setAttendants(responseData.attendants);

      const attendantoptions = responseData.attendants.map((attendant) => {
        return { value: attendant.id, label: attendant.name };
      });
      setAttendantOptions(attendantoptions);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertAttendant = (attendant) => {
    setAttendants([attendant, ...attendants]);
    setAttendantOptions([...attendantOptions, { value: attendant.id, label: attendant.name }]);
  };

  const UpdateAttendant = (attendant) => {
    const index = attendants.findIndex((x) => x.id === attendant.id);
    const newAttendants = [...attendants];
    newAttendants[index] = attendant;
    setAttendants([...newAttendants]);

    setAttendantOptions(
      attendantOptions.map((item) => {
        if (item.value === attendant.id) {
          return { value: attendant.id, label: attendant.name };
        }
        return item;
      }),
    );
  };

  const DeleteAttendant = (attendantId) => {
    const newAttendants = attendants.filter((attendant) => attendant.id !== attendantId);
    setAttendants([...newAttendants]);
    setAttendantOptions(attendantOptions.filter((item) => item.value !== attendantId));
  };

  const RetrieveCustodies = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      console.log("RetrieveCustodies called");
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/custodies`);
      console.log("Custodies response:", responseData);
      setCustodies(responseData.custodies);
    } catch (err) {
      console.error("Error in RetrieveCustodies:", err);
    }
    return true;
  }, [sendRequest, hasAccess]);

  const InsertCustody = (custody) => {
    setCustodies([custody, ...custodies]);
  };

  const UpdateCustody = (custody) => {
    const index = custodies.findIndex((x) => x.id === custody.id);
    const newCustodies = [...custodies];
    newCustodies[index] = custody;
    setCustodies([...newCustodies]);
  };

  const DeleteCustody = (custodyId) => {
    const newCustodies = custodies.filter((custody) => custody.id !== custodyId);
    setCustodies([...newCustodies]);
  };

  const RetrieveAccidents = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/vehicle-accidents`);
      setAccidents(responseData.accidents);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertAccident = (accident) => {
    setAccidents([accident, ...accidents]);
  };

  const UpdateAccident = (accident) => {
    const index = accidents.findIndex((x) => x.id === accident.id);
    const newAccidents = [...accidents];
    newAccidents[index] = accident;
    setAccidents([...newAccidents]);
  };

  const DeleteAccident = (accidentId) => {
    const newAccidents = accidents.filter((accident) => accident.id !== accidentId);
    setAccidents([...newAccidents]);
  };

  const RetrieveMaintenances = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/vehicle-maintenances`);
      setMaintenances(responseData.maintenances || []);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertMaintenance = (maintenance) => {
    setMaintenances([maintenance, ...maintenances]);
  };

  const UpdateMaintenance = (maintenance) => {
    const index = maintenances.findIndex((x) => x.id === maintenance.id);
    const newMaintenances = [...maintenances];
    newMaintenances[index] = maintenance;
    setMaintenances([...newMaintenances]);
  };

  const DeleteMaintenance = (maintenanceId) => {
    const newMaintenances = maintenances.filter((maintenance) => maintenance.id !== maintenanceId);
    setMaintenances([...newMaintenances]);
  };

  const RetrieveThirdParties = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/third-party-contacts`);
      setThirdParties(responseData.thirdParties || []);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertThirdParty = (thirdParty) => {
    setThirdParties([thirdParty, ...thirdParties]);
  };

  const UpdateThirdParty = (thirdParty) => {
    const index = thirdParties.findIndex((x) => x.id === thirdParty.id);
    const newItems = [...thirdParties];
    newItems[index] = thirdParty;
    setThirdParties([...newItems]);
  };

  const DeleteThirdParty = (thirdPartyId) => {
    const newItems = thirdParties.filter((item) => item.id !== thirdPartyId);
    setThirdParties([...newItems]);
  };

  const RetrieveFuels = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/fuels`);
      setFuels(responseData.fuels);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertFuel = (fuel) => {
    setFuels([fuel, ...fuels]);
  };

  const UpdateFuel = (fuel) => {
    const index = fuels.findIndex((x) => x.id === fuel.id);
    const newFuels = [...fuels];
    newFuels[index] = fuel;
    setFuels([...newFuels]);
  };

  const DeleteFuel = (fuelId) => {
    const newFuels = fuels.filter((fuel) => fuel.id !== fuelId);
    setFuels([...newFuels]);
  };

  const RetrieveStocks = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/stock`);
      setStocks(responseData.stocks || []);

      const stockoptions = (responseData.stocks || []).map((stock) => {
        return {
          value: stock.id,
          label: `${stock.name} (${stock.stockNumber})`,
        };
      });
      setStockOptions(stockoptions);
    } catch (err) {
      console.error("Stocks retrieve error:", err);
    }
    return true;
  }, [sendRequest, hasAccess]);

  const InsertStock = (stock) => {
    setStocks([stock, ...stocks]);
    setStockOptions([...stockOptions, { value: stock.id, label: `${stock.name} (${stock.stockNumber})` }]);
  };

  const UpdateStock = (stock) => {
    const index = stocks.findIndex((x) => x.id === stock.id);
    const newStocks = [...stocks];
    newStocks[index] = stock;
    setStocks([...newStocks]);

    setStockOptions(
      stockOptions.map((item) => {
        if (item.value === stock.id) {
          return { value: stock.id, label: `${stock.name} (${stock.stockNumber})` };
        }
        return item;
      }),
    );
  };

  const DeleteStock = (stockId) => {
    const newStocks = stocks.filter((stock) => stock.id !== stockId);
    setStocks([...newStocks]);
    setStockOptions(stockOptions.filter((item) => item.value !== stockId));
  };

  // Stock Movements functions
  const RetrieveStockMovements = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/stock-movements`);
      setStockMovements(responseData.movements || []);
    } catch (err) {
      console.error("Stock movements retrieve error:", err);
    }
    return true;
  }, [sendRequest, hasAccess]);

  const InsertStockMovement = (movement) => {
    setStockMovements([movement, ...stockMovements]);
  };

  const UpdateStockMovement = (movement) => {
    const index = stockMovements.findIndex((x) => x.id === movement.id);
    const newMovements = [...stockMovements];
    newMovements[index] = movement;
    setStockMovements([...newMovements]);
  };

  const DeleteStockMovement = (movementId) => {
    const newMovements = stockMovements.filter((movement) => movement.id !== movementId);
    setStockMovements([...newMovements]);
  };

  const RetrieveAutomations = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/automation-definitions`);
      setAutomations(responseData.automations);
      setAutomationOptions(
        responseData.automations.map((automation) => {
          return { value: automation.id, label: automation.name };
        }),
      );
    } catch (err) {}
    return true;
  }, [sendRequest]);

  const DeleteAutomation = (automationId) => {
    const newAutomations = automations.filter((automation) => automation.id !== automationId);
    setAutomations([...newAutomations]);
    setAutomationOptions(automationOptions.filter((item) => item.value !== automationId));
  };

  const InsertAutomation = (automation) => {
    setAutomations([automation, ...automations]);
    setAutomationOptions([...automationOptions, { value: automation.id, label: automation.name }]);
  };

  const UpdateAutomation = (automation) => {
    const index = automations.findIndex((x) => x.id === automation.id);
    const newAutomations = [...automations];
    newAutomations[index] = automation;
    setAutomations([...newAutomations]);

    setAutomationOptions(
      automationOptions.map((item) => {
        if (item.value === automation.id) {
          return { value: automation.id, label: automation.name };
        }
        return item;
      }),
    );
  };

  const DeleteDriver = (driverId) => {
    const newDrivers = drivers.filter((driver) => driver.id !== driverId);
    setDrivers([...newDrivers]);
    setDriverOptions(driverOptions.filter((item) => item.value !== driverId));
  };

  const InsertDriver = (driver) => {
    setDrivers([driver, ...drivers]);
    setDriverOptions([...driverOptions, { value: driver.id, label: driver.name }]);
  };

  const UpdateDriver = (driver) => {
    const index = drivers.findIndex((x) => x.id === driver.id);
    const newDrivers = [...drivers];
    newDrivers[index] = driver;
    setDrivers([...newDrivers]);
    setDriverOptions(
      driverOptions.map((item) => {
        if (item.value === driver.id) {
          return { value: driver.id, label: driver.name };
        }
        return item;
      }),
    );
  };

  const createRelationMaps = (relations) =>
    relations.reduce(
      (acc, relation) => {
        if (relation?.student && relation?.parent) {
          // Initialize student-to-parent map entry if not present
          if (!acc.studentToParent[relation.student.toString()]) {
            acc.studentToParent[relation.student.toString()] = [];
          }
          acc.studentToParent[relation.student.toString()].push(relation.parent.toString());

          // Initialize parent-to-student map entry if not present
          if (!acc.parentToStudent[relation.parent.toString()]) {
            acc.parentToStudent[relation.parent.toString()] = [];
          }
          acc.parentToStudent[relation.parent.toString()].push(relation.student.toString());
        }
        return acc;
      },
      { studentToParent: {}, parentToStudent: {} },
    );

  const UpdateRelations = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_STUDENTS)) {
      return true; // No permission, skip request
    }
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/relations`);
      let allRelations = responseData.relations;
      setRelations(allRelations);

      const { studentToParent, parentToStudent } = createRelationMaps(allRelations);
      setStudentToParentMap(studentToParent);
      setParentToStudentMap(parentToStudent);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertRelation = (relation) => {
    // setRelations([relation, ...relations]);
    setRelations((prevRelations) => [relation, ...prevRelations]);

    setStudentToParentMap((prevMap) => ({
      ...prevMap,
      [relation.student.toString()]: [...(prevMap[relation.student.toString()] || []), relation.parent.toString()],
    }));

    setParentToStudentMap((prevMap) => ({
      ...prevMap,
      [relation.parent.toString()]: [...(prevMap[relation.parent.toString()] || []), relation.student.toString()],
    }));
  };

  const UpdateRelation = (relation) => {
    // const index = relations.findIndex((x) => x.id === relation.id);
    // const newRelations = [...relations];
    // newRelations[index] = relation;
    // setRelations([...newRelations]);
    setRelations((prevRelations) => prevRelations.map((rel) => (rel.id === relation.id ? relation : rel)));

    let oldRelation = relations.find((rel) => rel.id === relation.id);

    setStudentToParentMap((prevMap) => {
      const studentId = relation.student.toString();
      const parentId = relation.parent.toString();

      // Remove old parent if it has changed

      const updatedParents = prevMap[studentId]?.filter((id) => id !== oldRelation.parent.toString()) || [];
      if (!updatedParents.includes(parentId)) {
        updatedParents.push(parentId);
      }

      return { ...prevMap, [studentId]: updatedParents };
    });

    setParentToStudentMap((prevMap) => {
      const studentId = relation.student.toString();
      const parentId = relation.parent.toString();

      // Remove old student if it has changed
      const updatedStudents = prevMap[parentId]?.filter((id) => id !== oldRelation.student.toString()) || [];
      if (!updatedStudents.includes(studentId)) {
        updatedStudents.push(studentId);
      }

      return { ...prevMap, [parentId]: updatedStudents };
    });
  };

  const DeleteRelation = (relationId) => {
    // const newRelations = relations.filter((relation) => relation.id !== relationId);
    // setRelations([...newRelations]);
    setRelations((prevRelations) => prevRelations.filter((relation) => relation.id !== relationId));

    const relationToDelete = relations.find((relation) => relation.id === relationId);
    if (relationToDelete) {
      setStudentToParentMap((prevMap) => {
        const updatedStudentParents = (prevMap[relationToDelete.student.toString()] || []).filter(
          (parentId) => parentId !== relationToDelete.parent.toString(),
        );
        return { ...prevMap, [relationToDelete.student.toString()]: updatedStudentParents };
      });

      setParentToStudentMap((prevMap) => {
        const updatedParentStudents = (prevMap[relationToDelete.parent.toString()] || []).filter(
          (studentId) => studentId !== relationToDelete.student.toString(),
        );
        return { ...prevMap, [relationToDelete.parent.toString()]: updatedParentStudents };
      });
    }
  };

  const InsertUser = (user) => {
    setUsers([user, ...users]);
    setUserOptions([...userOptions, { value: user.id, label: user.name }]);

    setUserMap((prevUserMap) => ({ ...prevUserMap, [user.id]: user }));

    if (user?.isshift) {
      setShiftUsers([...shiftUsers, user]);
      setShiftUserOptions([...shiftUserOptions, { value: user.id, label: user.name }]);
    }

    if (user?.usertype === "2") {
      setParentOptions([
        ...parentOptions,
        { value: user.id, label: user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name },
      ]);
    }

    if (user?.usertype === "10") {
      setPersonelOptions([
        ...personelOptions,
        { value: user.id, label: user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name },
      ]);
    }
  };

  const UpdateUser = (user) => {
    const index = users.findIndex((x) => x.id === user.id);
    const newUsers = [...users];
    newUsers[index] = user;
    setUsers([...newUsers]);

    setUserMap((prevUserMap) => ({ ...prevUserMap, [user.id]: user }));

    setUserOptions(
      userOptions.map((item) => {
        if (item.value === user.id) {
          return { value: user.id, label: user.name };
        }
        return item;
      }),
    );

    // Keep shift collections in sync
    if (user?.isshift) {
      const shiftUserIndex = shiftUsers.findIndex((x) => x.id === user.id);
      if (shiftUserIndex !== -1) {
        const newShiftUsers = [...shiftUsers];
        newShiftUsers[shiftUserIndex] = user;
        setShiftUsers([...newShiftUsers]);
      } else {
        setShiftUsers([...shiftUsers, user]);
      }
      setShiftUserOptions((prev) => {
        const exists = prev.some((item) => item.value === user.id);
        if (exists) {
          return prev.map((item) => (item.value === user.id ? { value: user.id, label: user.name } : item));
        }
        return [...prev, { value: user.id, label: user.name }];
      });
    } else {
      if (shiftUsers.map((item) => item.id).includes(user.id)) {
        setShiftUsers(shiftUsers.filter((u) => u.id !== user.id));
        setShiftUserOptions(shiftUserOptions.filter((item) => item.value !== user.id));
      }
    }

    // Keep parent options in sync
    setParentOptions((prev) => {
      if (user?.usertype === "2") {
        const exists = prev.some((item) => item.value === user.id);
        const label = user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name;
        if (exists) {
          return prev.map((item) => (item.value === user.id ? { value: user.id, label } : item));
        }
        return [...prev, { value: user.id, label }];
      }
      return prev.filter((item) => item.value !== user.id);
    });

    // Keep personel options in sync
    setPersonelOptions((prev) => {
      if (user?.usertype === "10") {
        const exists = prev.some((item) => item.value === user.id);
        const label = user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name;
        if (exists) {
          return prev.map((item) => (item.value === user.id ? { value: user.id, label } : item));
        }
        return [...prev, { value: user.id, label }];
      }
      return prev.filter((item) => item.value !== user.id);
    });
  };

  const DeleteUser = (userId) => {
    const newUsers = users.filter((user) => user.id !== userId);
    setUsers([...newUsers]);

    // Remove user from userMap
    setUserMap((prevUserMap) => {
      const newUserMap = { ...prevUserMap };
      delete newUserMap[userId];
      return newUserMap;
    });

    setUserOptions(userOptions.filter((item) => item.value !== userId));

    // Remove from parent/personel option lists as well
    setParentOptions(parentOptions.filter((item) => item.value !== userId));
    setPersonelOptions(personelOptions.filter((item) => item.value !== userId));

    if (shiftUsers.map((item) => item.id).includes(userId)) {
      setShiftUsers(shiftUsers.filter((user) => user.id !== userId));
      setShiftUserOptions(shiftUserOptions.filter((item) => item.value !== userId));
    }
  };

  const InsertShift = (shift) => {
    setShifts([shift, ...shifts]);
    setShiftOptions([
      ...shiftOptions,
      {
        value: shift.id,
        label: buildShiftLabel(shift),
      },
    ]);
  };

  const UpdateShift = (shift) => {
    const index = shifts.findIndex((x) => x.id === shift.id);
    const newShifts = [...shifts];
    newShifts[index] = shift;
    setShifts([...newShifts]);
    setShiftOptions(
      shiftOptions.map((item) => {
        if (item.value === shift.id) {
          return {
            value: shift.id,
            label: buildShiftLabel(shift),
          };
        }
        return item;
      }),
    );
  };

  const DeleteShift = (shiftId) => {
    const newShifts = shifts.filter((shift) => shift.id !== shiftId);
    setShifts([...newShifts]);
    setShiftOptions(shiftOptions.filter((item) => item.value !== shiftId));
  };

  const InsertStop = (stop) => {
    setStops([stop, ...stops]);
    setStopOptions([...stopOptions, { value: stop.id, label: stop.name }]);
  };

  const UpdateStop = (stop) => {
    const index = stops.findIndex((x) => x.id === stop.id);
    const newStops = [...stops];
    newStops[index] = stop;
    setStops([...newStops]);
    setStopOptions(
      stopOptions.map((item) => {
        if (item.value === stop.id) {
          return { value: stop.id, label: stop.name };
        }
        return item;
      }),
    );
  };

  const DeleteStop = (stopId) => {
    const newStops = stops.filter((stop) => stop.id !== stopId);
    setStops([...newStops]);
    setStopOptions(stopOptions.filter((item) => item.value !== stopId));
  };

  const UpdateUsers = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/users`);

      let allUsers = responseData.users;

      const useroptions = allUsers.map((user) => {
        return { value: user.id, label: user.name };
      });
      setUserOptions(useroptions);

      setUsers(allUsers);
      setUserMap(
        allUsers.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {}),
      );

      setShiftUsers(allUsers.filter((user) => user?.isshift));
      setShiftUserOptions(
        allUsers
          .filter((user) => user?.isshift)
          .map((user) => {
            return { value: user.id, label: user.name };
          }),
      );

      const allparents = allUsers.filter((user) => user.usertype === "2");
      const allpersonels = allUsers.filter((user) => user.usertype === "10");

      const parentoptions = allparents.map((user) => {
        return {
          value: user.id,
          label: user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name,
        };
      });

      const personeloptions = allpersonels.map((user) => {
        return {
          value: user.id,
          label: user.phonenumber ? user.name + " (" + user.phonenumber + ")" : user.name,
        };
      });
      setParentOptions(parentoptions);
      setPersonelOptions(personeloptions);
    } catch (err) {}

    return true;
  }, [sendRequest]);

  const UpdateDrivers = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/users/alldrivers`);
      const allDrivers = responseData.users;

      setDrivers(allDrivers);
      const driveroptions = allDrivers.map((user) => {
        return { value: user.id, label: user.name };
      });
      setDriverOptions(driveroptions);
    } catch (err) {}

    return true;
  }, [sendRequest]);

  const UpdateVehicles = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_INVENTORY)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/vehicles`);
      setVehicles(responseData.vehicles);

      const vehicleoptions = responseData.vehicles.map((vehicle) => {
        return { value: vehicle.id, label: vehicle.plate };
      });
      setVehicleOptions(vehicleoptions);
    } catch (err) {}

    return true;
  }, [sendRequest, hasAccess]);

  const UpdateCompanies = useCallback(async () => {
    const fetchCompanies = async () => {
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/companies`);

        setCompanies(responseData.companies);

        const companyoptions = responseData.companies.map((company) => {
          return { value: company.id, label: company.name };
        });
        setCompanyOptions(companyoptions);
      } catch (err) {}
    };
    await fetchCompanies();
  }, [sendRequest]);

  const UpdateSchools = useCallback(async () => {
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/schools`);
      let allSchools = responseData.schools;
      allSchools.sort((a, b) => a.name.localeCompare(b.name));
      schoolsREF.current = allSchools;
      setSchools(allSchools);

      const schooloptions = allSchools.map((schoolitem) => {
        return { value: schoolitem.id, label: schoolitem.name };
      });
      setSchoolOptions(schooloptions);

      setStudentSchools(allSchools.filter((school) => school?.type.toString() === "1"));
      setPersonelSchools(allSchools.filter((school) => school?.type.toString() === "2"));

      setStudentSchoolOptions(
        allSchools
          .filter((school) => school?.type.toString() === "1")
          .map((school) => {
            return { value: school.id, label: school.name };
          }),
      );

      setPersonelSchoolOptions(
        allSchools
          .filter((school) => school?.type.toString() === "2")
          .map((school) => {
            return { value: school.id, label: school.name };
          }),
      );
    } catch (err) {}
    return true;
  }, [sendRequest]);

  const InsertSchool = (school) => {
    setSchools([school, ...schools]);
    setSchoolOptions([...schoolOptions, { value: school.id, label: school.name }]);

    if (school.type.toString() === "1") {
      setStudentSchools((prev) => [school, ...prev]);
      setStudentSchoolOptions((prev) => [...prev, { value: school.id, label: school.name }]);
    } else if (school.type.toString() === "2") {
      setPersonelSchools((prev) => [school, ...prev]);
      setPersonelSchoolOptions((prev) => [...prev, { value: school.id, label: school.name }]);
    }
  };

  const UpdateSchool = (school) => {
    const index = schools.findIndex((x) => x.id === school.id);
    const newSchools = [...schools];
    newSchools[index] = school;
    setSchools([...newSchools]);
    setSchoolOptions(
      schoolOptions.map((item) => {
        if (item.value === school.id) {
          return { value: school.id, label: school.name };
        }
        return item;
      }),
    );

    if (school.type.toString() === "1") {
      const studentSchoolIndex = studentSchools.findIndex((x) => x.id === school.id);
      if (studentSchoolIndex !== -1) {
        const newStudentSchools = [...studentSchools];
        newStudentSchools[studentSchoolIndex] = school;
        setStudentSchools([...newStudentSchools]);
      }
      setStudentSchoolOptions(
        studentSchoolOptions.map((item) => {
          if (item.value === school.id) {
            return { value: school.id, label: school.name };
          }
          return item;
        }),
      );
    } else if (school.type.toString() === "2") {
      const personelSchoolIndex = personelSchools.findIndex((x) => x.id === school.id);
      if (personelSchoolIndex !== -1) {
        const newPersonelSchools = [...personelSchools];
        newPersonelSchools[personelSchoolIndex] = school;
        setPersonelSchools([...newPersonelSchools]);
      }
      setPersonelSchoolOptions(
        personelSchoolOptions.map((item) => {
          if (item.value === school.id) {
            return { value: school.id, label: school.name };
          }
          return item;
        }),
      );
    }
  };

  const DeleteSchool = (schoolId) => {
    const newSchools = schools.filter((school) => school.id !== schoolId);
    setSchools([...newSchools]);
    setSchoolOptions(schoolOptions.filter((item) => item.value !== schoolId));

    if (studentSchools.map((item) => item.id).includes(schoolId)) {
      const newStudentSchools = studentSchools.filter((school) => school.id !== schoolId);
      setStudentSchools([...newStudentSchools]);
      setStudentSchoolOptions(studentSchoolOptions.filter((item) => item.value !== schoolId));
    }

    if (personelSchools.map((item) => item.id).includes(schoolId)) {
      const newPersonelSchools = personelSchools.filter((school) => school.id !== schoolId);
      setPersonelSchools([...newPersonelSchools]);
      setPersonelSchoolOptions(personelSchoolOptions.filter((item) => item.value !== schoolId));
    }
  };

  const UpdateShifts = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_SHIFTS)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/shifts`);

      setShifts(responseData.shifts);

      const shiftoptions = responseData.shifts.map((shiftitem) => {
        return {
          value: shiftitem.id,
          label: buildShiftLabel(shiftitem),
        };
      });
      setShiftOptions(shiftoptions);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const UpdateStops = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_STOPS)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/stops`);
      setStops(responseData.stops);

      const stopoptions = responseData.stops.map((item) => {
        return { value: item.id, label: item.name };
      });
      setStopOptions(stopoptions);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const UpdatePersonelServices = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_PERSONELS)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/personelservices`);

      setStandartPersonelServices(responseData.personelservices.filter((item) => !item?.isshift));
      setShiftPersonelServices(responseData.personelservices.filter((item) => item?.isshift));

      setStandartPersonelServiceOptions(
        responseData.personelservices
          .filter((item) => !item?.isshift)
          .map((item) => {
            return { value: item.id, label: item.name };
          }),
      );
      setShiftPersonelServiceOptions(
        responseData.personelservices
          .filter((item) => item?.isshift)
          .map((item) => {
            return { value: item.id, label: item.name };
          }),
      );
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertPersonelService = (service) => {
    if (service?.isshift) {
      setShiftPersonelServices((prevServices) => [service, ...prevServices]);
      setShiftPersonelServiceOptions((prevOptions) => [...prevOptions, { value: service.id, label: service.name }]);
    } else {
      setStandartPersonelServices((prevServices) => [service, ...prevServices]);
      setStandartPersonelServiceOptions((prevOptions) => [...prevOptions, { value: service.id, label: service.name }]);
    }
  };

  const UpdatePersonelService = (service) => {
    if (service?.isshift) {
      const index = shiftPersonelServices.findIndex((x) => x.id === service.id);
      const newShiftPersonelServices = [...shiftPersonelServices];
      newShiftPersonelServices[index] = service;
      setShiftPersonelServices([...newShiftPersonelServices]);
      setShiftPersonelServiceOptions(
        shiftPersonelServiceOptions.map((item) => {
          if (item.value === service.id) {
            return { value: service.id, label: service.name };
          }
          return item;
        }),
      );
    } else {
      const index = standartPersonelServices.findIndex((x) => x.id === service.id);
      const newStandartPersonelServices = [...standartPersonelServices];
      newStandartPersonelServices[index] = service;
      setStandartPersonelServices([...newStandartPersonelServices]);
      setStandartPersonelServiceOptions(
        standartPersonelServiceOptions.map((item) => {
          if (item.value === service.id) {
            return { value: service.id, label: service.name };
          }
          return item;
        }),
      );
    }
  };

  const DeletePersonelService = (serviceId) => {
    if (shiftPersonelServices.map((item) => item.id).includes(serviceId)) {
      const newShiftPersonelServices = shiftPersonelServices.filter((service) => service.id !== serviceId);
      setShiftPersonelServices([...newShiftPersonelServices]);
      setShiftPersonelServiceOptions(shiftPersonelServiceOptions.filter((item) => item.value !== serviceId));
    }
    if (standartPersonelServices.map((item) => item.id).includes(serviceId)) {
      const newStandartPersonelServices = standartPersonelServices.filter((service) => service.id !== serviceId);
      setStandartPersonelServices([...newStandartPersonelServices]);
      setStandartPersonelServiceOptions(standartPersonelServiceOptions.filter((item) => item.value !== serviceId));
    }
  };

  const UpdateVehicleRoutes = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_STUDENTS)) {
      return true; // No permission, skip request
    }
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/vehicleroutes`);

      let allVehicleRoutes = responseData.vehicleRoutes;
      const vehiclerouteoptions = allVehicleRoutes
        .filter((route) => route?.type?.toString() === "100")
        .map((vehicleroute) => {
          return {
            value: vehicleroute.id,
            label: vehicleroute?.school?.name + " - " + vehicleroute?.name,
          };
        });

      setVehicleRouteOptions(vehiclerouteoptions.sort((a, b) => a.label.localeCompare(b.label)));
      setVehicleRoutes(allVehicleRoutes);
    } catch (err) {
      console.log(err);
    }
    return true;
  }, [sendRequest, hasAccess]);

  const InsertVehicleRoute = (vehicleRoute) => {
    setVehicleRoutes([vehicleRoute, ...vehicleRoutes]);
    setVehicleRouteOptions([
      ...vehicleRouteOptions,
      {
        value: vehicleRoute.id,
        label: vehicleRoute.school.name + " - " + vehicleRoute.name,
      },
    ]);
  };

  const UpdateVehicleRoute = (vehicleRoute) => {
    const index = vehicleRoutes.findIndex((x) => x.id === vehicleRoute.id);
    const newVehicleRoutes = [...vehicleRoutes];
    newVehicleRoutes[index] = vehicleRoute;
    setVehicleRoutes([...newVehicleRoutes]);
    setVehicleRouteOptions(
      vehicleRouteOptions.map((item) => {
        if (item.value === vehicleRoute.id) {
          return {
            value: vehicleRoute.id,
            label: vehicleRoute.school.name + " - " + vehicleRoute.name,
          };
        }
        return item;
      }),
    );
  };

  const DeleteVehicleRoute = (vehicleRouteId) => {
    const newVehicleRoutes = vehicleRoutes.filter((vehicleRoute) => vehicleRoute.id !== vehicleRouteId);
    setVehicleRoutes([...newVehicleRoutes]);
    setVehicleRouteOptions(vehicleRouteOptions.filter((item) => item.value !== vehicleRouteId));
  };

  // Bundled (multi-school) routes live in their own collection. We load them into the store at boot
  // ONLY when the company has the feature, so the list/dashboard read them instantly (no per-mount
  // fetch) while feature-off companies pay nothing.
  const UpdateBundledRoutes = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_STUDENTS)) {
      return true; // No permission, skip request
    }
    if (!auth?.company?.features?.[COMPANY_FEATURES.BUNDLED_STUDENT_ROUTES]) {
      return true; // Feature off, skip request
    }
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/bundled-route`);
      setBundledRoutes(Array.isArray(responseData?.bundledRoutes) ? responseData.bundledRoutes : []);
    } catch (err) {
      console.log(err);
    }
    return true;
  }, [sendRequest, hasAccess, auth?.company?.features]);

  const InsertBundledRoute = (bundledRoute) => {
    setBundledRoutes((prev) => [bundledRoute, ...prev]);
  };

  const UpdateBundledRoute = (bundledRoute) => {
    setBundledRoutes((prev) => prev.map((route) => (route.id === bundledRoute.id ? bundledRoute : route)));
  };

  const DeleteBundledRoute = (bundledRouteId) => {
    setBundledRoutes((prev) => prev.filter((route) => route.id !== bundledRouteId));
  };

  const RetrieveContracts = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/contracts`);
      setContracts(responseData.contracts);

      const allContracts = responseData.contracts.map((contract) => {
        const payerName = contract.payerName;
        const year = contract.year ? getContractYearLabel(contract.year.toString()) : "?";
        const studentName = contract?.student?.name ? contract.student.name : "?";

        const label = `${studentName}${payerName?.length ? ` - ${payerName}` : ""} - ${year}`;

        return {
          value: contract.id,
          label,
        };
      });
      setContractsOptions(allContracts);
    } catch (err) {
      setContractsOptions([]);
      setContracts([]);
    }
    return true;
  }, [sendRequest, hasAccess]);

  const InsertContract = (contract) => {
    setContracts([contract, ...contracts]);
    const payerName = contract.payerName || "?";
    const year = contract.year ? getContractYearLabel(contract.year.toString()) : "?";
    const studentName = contract?.student?.name ? contract.student.name : "?";

    const label = `${studentName}${payerName?.length ? ` - ${payerName}` : ""} - ${year}`;

    setContractsOptions([
      ...contractsOptions,
      {
        value: contract.id,
        label,
      },
    ]);
  };

  const UpdateContract = (contract) => {
    const index = contracts.findIndex((x) => x.id === contract.id);
    const newContracts = [...contracts];
    newContracts[index] = contract;
    setContracts([...newContracts]);

    const payerName = contract.payerName || "?";
    const year = contract.year ? getContractYearLabel(contract.year.toString()) : "?";
    const studentName = contract?.student?.name ? contract.student.name : "?";

    const label = `${studentName}${payerName?.length ? ` - ${payerName}` : ""} - ${year}`;

    setContractsOptions([
      ...contractsOptions,
      {
        value: contract.id,
        label,
      },
    ]);
  };

  const DeleteContract = (contractId) => {
    const newContracts = contracts.filter((contract) => contract.id !== contractId);
    setContracts([...newContracts]);
    setContractsOptions(contractsOptions.filter((item) => item.value !== contractId));
  };

  const UpdateTransfers = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_TRANSFERS)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/transfers`);
      setTransfers(responseData.transfers);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertTransfer = (transfer) => {
    setTransfers([transfer, ...transfers]);
  };

  const UpdateTransfer = (transfer) => {
    const index = transfers.findIndex((x) => x.id === transfer.id);
    const newTransfers = [...transfers];
    newTransfers[index] = transfer;
    setTransfers([...newTransfers]);
  };

  const DeleteTransfer = (transferId) => {
    const vehicleRouteIds = transfers.find((transfer) => transfer.id === transferId).vehicleRoutes;
    const newTransfers = transfers.filter((transfer) => transfer.id !== transferId);
    setTransfers([...newTransfers]);

    //remove vehicleRouteIds from vehicleRoutes
    const newVehicleRoutes = vehicleRoutes.filter((vehicleRoute) => !vehicleRouteIds.includes(vehicleRoute.id));
    setVehicleRoutes([...newVehicleRoutes]);

    //remove vehicleRouteIds from vehicleRouteOptions
    setVehicleRouteOptions(vehicleRouteOptions.filter((item) => !vehicleRouteIds.includes(item.value)));

    const newPersonelServices = standartPersonelServices.filter(
      (personelService) => !vehicleRouteIds.includes(personelService.id),
    );

    setStandartPersonelServices([...newPersonelServices]);

    setStandartPersonelServiceOptions(
      standartPersonelServiceOptions.filter((item) => !vehicleRouteIds.includes(item.value)),
    );
  };

  const UpdateTransferregions = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_TRANSFERS)) {
      return true;
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/transferregions`);
      setTransferRegions(responseData.transferregions);

      const transferOptions = responseData.transferregions.map((regionitem) => {
        return { value: regionitem.id, label: regionitem.name };
      });
      setTransferRegionOptions(transferOptions);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const RetrieveReceipts = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/receipts`);
      setReceipts(responseData.receipts);
    } catch (err) {}

    return true;
  }, [sendRequest, hasAccess]);

  const InsertReceipt = (receipt) => {
    setReceipts([receipt, ...receipts]);
  };

  const UpdateReceipt = (receipt) => {
    const index = receipts.findIndex((x) => x.id === receipt.id);
    const newReceipts = [...receipts];
    newReceipts[index] = receipt;
    setReceipts([...newReceipts]);
  };

  const DeleteReceipt = (receiptId) => {
    const newReceipts = receipts.filter((receipt) => receipt.id !== receiptId);
    setReceipts([...newReceipts]);
  };

  const InsertServiceTemplate = (serviceTemplate) => {
    setServiceTemplates([serviceTemplate, ...serviceTemplates]);
    setServiceTemplateOptions([...serviceTemplateOptions, { value: serviceTemplate.id, label: serviceTemplate.name }]);
  };

  const UpdateServiceTemplate = (serviceTemplate) => {
    const index = serviceTemplates.findIndex((x) => x.id === serviceTemplate.id);
    const newServiceTemplates = [...serviceTemplates];
    newServiceTemplates[index] = serviceTemplate;
    setServiceTemplates([...newServiceTemplates]);
    setServiceTemplateOptions(
      serviceTemplateOptions.map((item) => {
        if (item.value === serviceTemplate.id) {
          return { value: serviceTemplate.id, label: serviceTemplate.name };
        }
        return item;
      }),
    );
  };

  const DeleteServiceTemplate = (serviceTemplateId) => {
    const newServiceTemplates = serviceTemplates.filter((serviceTemplate) => serviceTemplate.id !== serviceTemplateId);
    setServiceTemplates([...newServiceTemplates]);
    setServiceTemplateOptions(serviceTemplateOptions.filter((item) => item.value !== serviceTemplateId));
  };

  const InsertDiscount = (discount) => {
    setDiscounts([discount, ...discounts]);
    setDiscountOptions([...discountOptions, { value: discount.id, label: discount.name }]);
  };

  const UpdateDiscount = (discount) => {
    const index = discounts.findIndex((x) => x.id === discount.id);
    const newDiscounts = [...discounts];
    newDiscounts[index] = discount;
    setDiscounts([...newDiscounts]);
    setDiscountOptions(
      discountOptions.map((item) => {
        if (item.value === discount.id) {
          return { value: discount.id, label: discount.name };
        }
        return item;
      }),
    );
  };

  const DeleteDiscount = (discountId) => {
    const newDiscounts = discounts.filter((discount) => discount.id !== discountId);
    setDiscounts([...newDiscounts]);
    setDiscountOptions(discountOptions.filter((item) => item.value !== discountId));
  };

  const InsertExpense = (expense) => {
    setExpenses([expense, ...expenses]);
  };

  const UpdateExpense = (expense) => {
    const index = expenses.findIndex((x) => x.id === expense.id);
    const newExpenses = [...expenses];
    newExpenses[index] = expense;
    setExpenses([...newExpenses]);
  };

  const DeleteExpense = (expenseId) => {
    const newExpenses = expenses.filter((expense) => expense.id !== expenseId);
    setExpenses([...newExpenses]);
  };

  const InsertEducationYear = (educationYear) => {
    setEducationYears([educationYear, ...educationYears]);
    setEducationYearsOptions([...educationYearsOptions, { value: educationYear.id, label: educationYear.name }]);
  };

  const UpdateEducationYear = (educationYear) => {
    const index = educationYears.findIndex((x) => x.id === educationYear.id);
    const newEducationYears = [...educationYears];
    newEducationYears[index] = educationYear;
    setEducationYears([...newEducationYears]);
    setEducationYearsOptions(
      educationYearsOptions.map((item) => {
        if (item.value === educationYear.id) {
          return { value: educationYear.id, label: educationYear.name };
        }
        return item;
      }),
    );
  };

  const DeleteEducationYear = (educationYearId) => {
    const newEducationYears = educationYears.filter((educationYear) => educationYear.id !== educationYearId);
    setEducationYears([...newEducationYears]);
    setEducationYearsOptions(educationYearsOptions.filter((item) => item.value !== educationYearId));
    if (selectedEducationYearId === educationYearId) {
      setSelectedEducationYearId(null);
      localStorage.removeItem("selectedEducationYearId");
    }
  };

  const InsertWithdraw = (withdraw) => {
    setWithdraws([withdraw, ...withdraws]);
  };

  const UpdateWithdraw = (withdraw) => {
    const index = withdraws.findIndex((x) => x.id === withdraw.id);
    const newWithdraws = [...withdraws];
    newWithdraws[index] = withdraw;
    setWithdraws([...newWithdraws]);
  };

  const DeleteWithdraw = (withdrawId) => {
    const newWithdraws = withdraws.filter((withdraw) => withdraw.id !== withdrawId);
    setWithdraws([...newWithdraws]);
  };

  const RetrieveWithdraws = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/withdraws`);
      setWithdraws(responseData.withdraws);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const UpdateVehiclePayments = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }
    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/vehiclepayments`);
      setVehiclePayments(responseData.vehiclePayments);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const InsertVehiclePayment = (vehiclePayment) => {
    setVehiclePayments([vehiclePayment, ...vehiclePayments]);
  };

  const UpdateVehiclePayment = (vehiclePayment) => {
    const index = vehiclePayments.findIndex((x) => x.id === vehiclePayment.id);
    const newVehiclePayments = [...vehiclePayments];
    newVehiclePayments[index] = vehiclePayment;
    setVehiclePayments([...newVehiclePayments]);
  };

  const DeleteVehiclePayment = (vehiclePaymentId) => {
    const newVehiclePayments = vehiclePayments.filter((vehiclePayment) => vehiclePayment.id !== vehiclePaymentId);
    setVehiclePayments([...newVehiclePayments]);
  };

  const InsertTransferRegion = (transferRegion) => {
    setTransferRegions([transferRegion, ...transferregions]);
    setTransferRegionOptions([...transferregionOptions, { value: transferRegion.id, label: transferRegion.name }]);
  };

  const UpdateTransferRegion = (transferRegion) => {
    const index = transferregions.findIndex((x) => x.id === transferRegion.id);
    const newTransferRegions = [...transferregions];
    newTransferRegions[index] = transferRegion;
    setTransferRegions([...newTransferRegions]);
    setTransferRegionOptions(
      transferregionOptions.map((item) => {
        if (item.value === transferRegion.id) {
          return { value: transferRegion.id, label: transferRegion.name };
        }
        return item;
      }),
    );
  };

  const DeleteTransferRegion = (transferRegionId) => {
    const newTransferRegions = transferregions.filter((transferRegion) => transferRegion.id !== transferRegionId);
    setTransferRegions([...newTransferRegions]);
    setTransferRegionOptions(transferregionOptions.filter((item) => item.value !== transferRegionId));
  };

  const RetrieveExpenses = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/expenses`);
      setExpenses(responseData.expenses);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const RetrieveEducationYears = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/educationyears`);
      setEducationYears(responseData.educationYears);

      const allEducationYears = responseData.educationYears.map((educationyear) => {
        return {
          value: educationyear.id,
          label: educationyear.name,
        };
      });
      setEducationYearsOptions(allEducationYears);
      const storedEducationYearId = localStorage.getItem("selectedEducationYearId");
      if (storedEducationYearId) {
        setSelectedEducationYearId(storedEducationYearId);
      } else {
        setSelectedEducationYearId(allEducationYears[allEducationYears.length - 1].value);
      }
    } catch (err) {}

    return true;
  }, [sendRequest, hasAccess]);

  const RetrieveAccountingUtils = useCallback(async () => {
    if (!hasAccess(PERMISSION_TYPES.ACCESS_TO_ACCOUNTING)) {
      return true; // No permission, skip request
    }

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/discounts`);
      setDiscounts(responseData.discounts);
      const allDiscounts = responseData.discounts.map((discount) => {
        return {
          value: discount.id,
          label: discount.name,
        };
      });
      setDiscountOptions(allDiscounts);
    } catch (err) {}

    try {
      const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/servicetemplates`);
      setServiceTemplates(responseData.serviceTemplates);

      const allserviceTemplates = responseData.serviceTemplates.map((serviceTemplate) => {
        return {
          value: serviceTemplate.id,
          label: serviceTemplate.name,
        };
      });
      setServiceTemplateOptions(allserviceTemplates);
    } catch (err) {}
    return true;
  }, [sendRequest, hasAccess]);

  const UpdateSelectedEducationYearId = (selectedYearId) => {
    localStorage.setItem("selectedEducationYearId", selectedYearId);
    setSelectedEducationYearId(selectedYearId);
  };

  const InsertVehicle = (vehicle) => {
    setVehicles([vehicle, ...vehicles]);
    setVehicleOptions([{ value: vehicle.id, label: vehicle.plate }, ...vehicleOptions]);
  };

  const UpdateVehicle = (vehicle) => {
    const index = vehicles.findIndex((x) => x.id === vehicle.id);
    const newVehicles = [...vehicles];
    newVehicles[index] = vehicle;
    setVehicles([...newVehicles]);

    setVehicleOptions(
      vehicleOptions.map((item) => {
        if (item.value === vehicle.id) {
          return { value: vehicle.id, label: vehicle.plate };
        }
        return item;
      }),
    );
  };

  const DeleteVehicle = (vehicleId) => {
    const newVehicles = vehicles.filter((vehicle) => vehicle.id !== vehicleId);
    setVehicles([...newVehicles]);
    setVehicleOptions(vehicleOptions.filter((item) => item.value !== vehicleId));
  };

  const GetParentFromStudent = (studentId) => {
    const currentParents = relations
      .filter((relation) => relation.student === studentId)
      .map((relation) => {
        const parent = userMap[relation.parent];
        if (!parent) {
          return null; // Skip if parent not found
        }
        return parent;
      })
      .filter((parent) => parent !== null); // Remove null values from array

    if (currentParents.length > 0) {
      return currentParents[0];
    }

    // const student = students.find((student) => student.id === studentId);
    // if (student) {
    //   const relation = relations.find((relation) => relation.student === student.id);
    //   if (relation) {
    //     return users.find((user) => user.id === relation.parent);
    //   }
    // }
    return null;
  };

  const UpdateEverything = useCallback(async () => {
    const storedLang = localStorage.getItem("i18nextLng");
    i18next.changeLanguage(storedLang || "en");

    if (isCustomerContactRole(auth.client?.role)) {
      setStoreReady(true);
      return;
    }

    await Promise.all([
      RetrieveEducationYears(),
      UpdateUsers(),
      UpdateDrivers(),
      UpdateSchools(),
      UpdateStudents(),
      UpdateVehicleRoutes(),
      UpdateBundledRoutes(),
      UpdateRelations(),
      UpdateVehicles(),
      RetrieveDriverDetails(),
      RetrieveAttendants(),
      RetrieveCustodies(),
      RetrieveAccidents(),
      RetrieveMaintenances(),
      RetrieveThirdParties(),
      RetrieveFuels(),
      RetrieveStocks(),
      RetrieveStockMovements(),

      UpdateTransferregions(),
      UpdateTransfers(),

      UpdateStops(),
      UpdatePersonelServices(),
      UpdateShifts(),

      RetrieveAutomations(),
      RetrieveContracts(),
      RetrieveReceipts(),
      RetrieveWithdraws(),
      RetrieveExpenses(),
      RetrieveAccountingUtils(),
      UpdateVehiclePayments(),

      //UpdateCompanies(),
      //UpdateVehicles(); BU ZATEN YOKTU

      // Contracts
    ]);
    setStoreReady(true);
  }, [
    RetrieveEducationYears,
    UpdateUsers,
    UpdateDrivers,
    UpdateSchools,
    UpdateStudents,
    UpdateVehicleRoutes,
    UpdateBundledRoutes,
    UpdateRelations,
    UpdateTransferregions,
    UpdateTransfers,
    UpdateStops,
    UpdatePersonelServices,
    RetrieveAccidents,
    RetrieveMaintenances,
    RetrieveThirdParties,
    auth.client?.role,
  ]);

  return {
    isAdmin,
    users,
    userMap,
    studentToParentMap,
    parentToStudentMap,
    vehicles,
    schools,
    schoolOptions,
    studentSchools,
    studentSchoolOptions,
    personelSchools,
    personelSchoolOptions,
    stopOptions,
    companies,
    relations,
    students,
    studentOptions,
    parentOptions,
    personelOptions,
    companyOptions,
    vehicleRoutes,
    bundledRoutes,
    drivers,
    driverOptions,
    vehicleOptions,
    storeReady,
    contracts,
    educationYears,
    selectedEducationYearId,
    discounts,
    serviceTemplates,
    educationYearsOptions,
    discountOptions,
    serviceTemplateOptions,
    receipts,
    withdraws,
    contractsOptions,
    expenses,
    vehicleRouteOptions,
    vehiclePayments,
    transfers,

    transferregions,
    transferregionOptions,
    isProcessing,
    shifts,
    stops,
    standartPersonelServices,
    standartPersonelServiceOptions,
    shiftPersonelServices,
    shiftPersonelServiceOptions,
    userOptions,
    shiftOptions,
    shiftUsers,
    shiftUserOptions,
    InsertUser,
    UpdateUser,
    DeleteUser,
    InsertStudent,
    UpdateStudent,
    DeleteStudent,
    InsertRelation,
    UpdateRelation,
    DeleteRelation,
    InsertShift,
    UpdateShift,
    DeleteShift,
    InsertStop,
    UpdateStop,
    DeleteStop,
    InsertDriver,
    UpdateDriver,
    DeleteDriver,
    UpdatePersonelServices,
    InsertPersonelService,
    UpdatePersonelService,
    DeletePersonelService,
    InsertVehicleRoute,
    UpdateVehicleRoute,
    DeleteVehicleRoute,
    InsertBundledRoute,
    UpdateBundledRoute,
    DeleteBundledRoute,
    UpdateBundledRoutes,
    UpdateVehicles,
    UpdateRelations,
    UpdateVehicleRoutes,
    UpdateCompanies,
    UpdateSchools,
    InsertSchool,
    UpdateSchool,
    DeleteSchool,
    UpdateUsers,
    UpdateDrivers,
    UpdateStudents,
    UpdateEverything,
    RetrieveContracts,
    InsertContract,
    UpdateContract,
    DeleteContract,
    RetrieveAccountingUtils,
    RetrieveReceipts,
    RetrieveWithdraws,
    RetrieveExpenses,
    UpdateVehiclePayments,
    UpdateTransfers,
    UpdateTransferregions,
    UpdateShifts,
    UpdateStops,
    GetParentFromStudent,
    UpdateSelectedEducationYearId,
    RetrieveEducationYears,
    RetrieveAutomations,

    automations,
    automationOptions,
    InsertAutomation,
    UpdateAutomation,
    DeleteAutomation,

    InsertTransfer,
    UpdateTransfer,
    DeleteTransfer,

    InsertReceipt,
    UpdateReceipt,
    DeleteReceipt,

    InsertExpense,
    UpdateExpense,
    DeleteExpense,

    InsertServiceTemplate,
    UpdateServiceTemplate,
    DeleteServiceTemplate,

    InsertDiscount,
    UpdateDiscount,
    DeleteDiscount,

    InsertEducationYear,
    UpdateEducationYear,
    DeleteEducationYear,

    InsertWithdraw,
    UpdateWithdraw,
    DeleteWithdraw,

    InsertVehiclePayment,
    UpdateVehiclePayment,
    DeleteVehiclePayment,

    InsertTransferRegion,
    UpdateTransferRegion,
    DeleteTransferRegion,

    InsertVehicle,
    UpdateVehicle,
    DeleteVehicle,

    driverDetails,
    InsertDriverDetail,
    UpdateDriverDetail,
    DeleteDriverDetail,

    attendants,
    attendantOptions,
    RetrieveAttendants,
    InsertAttendant,
    UpdateAttendant,
    DeleteAttendant,

    custodies,
    RetrieveCustodies,
    InsertCustody,
    UpdateCustody,
    DeleteCustody,

    accidents,
    RetrieveAccidents,
    InsertAccident,
    UpdateAccident,
    DeleteAccident,
    maintenances,
    RetrieveMaintenances,
    InsertMaintenance,
    UpdateMaintenance,
    DeleteMaintenance,
    thirdParties,
    RetrieveThirdParties,
    InsertThirdParty,
    UpdateThirdParty,
    DeleteThirdParty,

    fuels,
    InsertFuel,
    UpdateFuel,
    DeleteFuel,

    stocks,
    stockOptions,
    RetrieveStocks,
    InsertStock,
    UpdateStock,
    DeleteStock,

    stockMovements,
    RetrieveStockMovements,
    InsertStockMovement,
    UpdateStockMovement,
    DeleteStockMovement,
  };
};
